import React, { useRef } from 'react';
import Joyride, { ACTIONS, CallBackProps, EVENTS, LIFECYCLE, STATUS, Step } from 'react-joyride';
import { useMount, useSetState } from 'react-use';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useDispatch, useSelector } from 'react-redux';
import { toggleDrawer } from '../../redux/appConfigurator/action';
import { isDrawerOpen } from '../../selectors/selectors';
import { setGuidedTourIndex, updateUserConfig } from '../../redux/configuration/action';
import { appGuideIndex } from '../../pages/EsSettings/selector';

/** Keep tour targets (and room for the tooltip) in view, including nested scroll parents. */
function scrollTourTargetIntoView(target: Step['target'] | undefined) {
    if (!target || target === 'body') return;
    const el =
        typeof target === 'string'
            ? (document.querySelector(target) as HTMLElement | null)
            : (target as HTMLElement | null);
    if (!el || typeof el.scrollIntoView !== 'function') return;
    // Center keeps bottom-of-page targets (e.g. Save Draft footer) visible with the tooltip above.
    el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
}

export interface ITutorialState {
    run: boolean;
    sidebarOpen?: boolean;
    stepIndex?: number;
    steps: Step[];
}

/** Per-tour Configuration boolean — dismissing one must never clear another. */
export type TourConfigField =
    | 'show_app_guide'
    | 'show_brand_guide'
    | 'show_campaign_creation_guide'
    | 'show_campaign_detail_guide'
    | 'show_participant_workspace_guide'
    | 'show_project_guide'
    | 'show_video_guide';

export interface ITutorialProps {
    stepsDetails: ITutorialState;
    onEnd?: () => void;
    /** When true, do not PATCH any tour flag (rare; prefer configField). */
    skipConfigUpdate?: boolean;
    /**
     * Required (unless skipConfigUpdate): which tour flag to set false on dismiss.
     * Each joyride must use its own field so hide/dismiss only blocks that tour.
     */
    configField?: TourConfigField;
}

const Tutorial: React.FC<ITutorialProps> = ({ stepsDetails, onEnd, skipConfigUpdate, configField }) => {
    const savedStepIndex = useSelector(appGuideIndex);
    const [{ run, sidebarOpen, stepIndex, steps }, setState] = useSetState<ITutorialState>({
        run: false,
        sidebarOpen: false,
        stepIndex: savedStepIndex ?? 0,
        steps: [],
    });
    const joyride = useRef<Joyride | null>(null);

    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    useMount(() => {
        setState(stepsDetails);
    });

    const menuSteps = ['Projects', 'Dashboard', 'Profile', 'Toggle Dark Mode'];
    const drawerOpen = useSelector(isDrawerOpen);

    const persistTourDismissed = () => {
        if (skipConfigUpdate) return;
        // Only flip the flag for THIS tour — never blanket-clear other guides.
        if (!configField) {
            if (process.env.NODE_ENV === 'development') {
                // eslint-disable-next-line no-console
                console.warn(
                    '[Tutorial] Missing configField — dismiss will not persist. Pass a unique show_*_guide flag.',
                );
            }
            return;
        }
        dispatch(updateUserConfig({ payload: { [configField]: false } }));
    };

    const handleJoyrideCallback = (data: CallBackProps) => {
        const { action, index, status, type, step, lifecycle } = data;
        if (action === ACTIONS.UPDATE || (action === ACTIONS.CLOSE && lifecycle === LIFECYCLE.COMPLETE)) {
            if (step.target === '#featured-contest') {
                dispatch(setGuidedTourIndex(index + 1));
            } else {
                dispatch(setGuidedTourIndex(index));
            }
        }
        if (type === EVENTS.STEP_BEFORE && action !== ACTIONS.CLOSE) {
            scrollTourTargetIntoView(step?.target);
            if (menuSteps.includes(step.title as string)) {
                if (!drawerOpen) {
                    setState({ run: false });
                    dispatch(toggleDrawer({ status: 'open' }));
                    setTimeout(() => {
                        setState({ run: true });
                    }, 200);
                }
            } else if (drawerOpen) {
                setState({ run: false });
                dispatch(toggleDrawer({ status: 'closed' }));
                setTimeout(() => {
                    setState({ run: true });
                }, 200);
            }
        }
        // Re-center after the tooltip mounts — handles late layout / sticky footers.
        if (type === EVENTS.TOOLTIP && action !== ACTIONS.CLOSE) {
            scrollTourTargetIntoView(step?.target);
        }
        // Don't show again / Finish / X close — all persist so tours never reappear on reload or new devices.
        // FINISHED/SKIPPED are handled first; CLOSE alone covers the X button (status can't be finished/skipped there).
        const dismissedPermanently =
            status === STATUS.FINISHED ||
            status === STATUS.SKIPPED ||
            action === ACTIONS.SKIP ||
            action === ACTIONS.CLOSE;
        if (dismissedPermanently) {
            onEnd && onEnd();
            setState({ run: false, stepIndex: 0 });
            dispatch(setGuidedTourIndex(0));
            if (drawerOpen) {
                dispatch(toggleDrawer({ status: 'closed' }));
            }
            persistTourDismissed();
        } else if (([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND] as string[]).includes(type)) {
            const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
            // to-do: review these steps with Victor
            if (sidebarOpen && index === 0) {
                setTimeout(() => {
                    setState({ run: true });
                }, 400);
            } else if (sidebarOpen && index === 1) {
                setState({
                    run: false,
                    sidebarOpen: false,
                    stepIndex: nextStepIndex,
                });

                setTimeout(() => {
                    setState({ run: true });
                }, 400);
            } else if (index === 2 && action === ACTIONS.PREV) {
                setState({
                    run: false,
                    sidebarOpen: true,
                    stepIndex: nextStepIndex,
                });

                setTimeout(() => {
                    setState({ run: true });
                }, 400);
            } else {
                // Update state to advance the tour
                setState({
                    sidebarOpen: false,
                    stepIndex: nextStepIndex,
                });
            }
        }
    };

    return (
        <Joyride
            callback={handleJoyrideCallback}
            continuous
            run={run}
            ref={joyride}
            showProgress={true}
            showSkipButton={true}
            disableScrolling={false}
            scrollToFirstStep
            scrollOffset={120}
            scrollDuration={400}
            stepIndex={stepIndex}
            steps={steps}
            styles={{
                options: {
                    arrowColor: theme[themeStyle].deep_grey,
                    backgroundColor: theme[themeStyle].deep_grey,
                    primaryColor: theme[themeStyle].highlight,
                    textColor: theme[themeStyle].tag_white,
                },
                tooltip: {
                    borderRadius: 10,
                },
                tooltipContainer: {
                    textAlign: 'left',
                    fontFamily: FONT_ENUM.REGULAR_HEADER,
                    fontSize: 13,
                },
                tooltipContent: {
                    fontSize: 13,
                    padding: '20px 0',
                },
                tooltipTitle: {
                    fontSize: 19,
                },
                buttonNext: {
                    borderRadius: 20,
                    padding: 10,
                    backgroundColor: theme[themeStyle].true_highlight,
                    fontSize: 12,
                    color: '#000',
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                },
                buttonSkip: {
                    color: theme[themeStyle].another_gray_color,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    padding: '20px 0',
                    fontSize: 13,
                },
                buttonBack: {
                    color: theme[themeStyle].primary_white,
                    fontSize: 12,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                },
            }}
            locale={{
                // String (not JSX) so react-joyride reliably fires ACTIONS.SKIP / STATUS.SKIPPED.
                skip: "Don't show again",
                last: 'Finish',
            }}
        />
    );
};

export { Tutorial };
